import { EQueryKeys } from '@enums/queryKeys.enum';
import type { GetAllAdminsParams } from '@services/admins/dtos/getAll.dto';
import type { GetAllBusinessLinesParams } from '@services/businessLines/dtos/getAll.dto';
import type { GetAllInterventionsParams } from '@services/interventions/dtos/getAll.dto';
import type { GetAllPartnershipsParams } from '@services/partnerships/dtos/getAll.dto';
import type { GetAllProvidersParams } from '@services/providers/dtos/getAll.dto';
import type { GetAllServiceRequestsParams } from '@services/serviceRequests/dtos/getAll.dto';
import type { GetAllServicesParams } from '@services/services/dtos/getAll.dto';
import type { GetSmartEventTimelineParams } from '@services/smartEvents/types/getTimeline.type';
import type { GetAllTasksParams } from '@services/tasks/dtos/getAll.dto';
import type { GetAllWorkersParams } from '@services/workers/dtos/getAll.dto';

export const queryKeys = {
  admins: {
    all: [EQueryKeys.Admins],
    detail: (id: string) => [EQueryKeys.AdminDetails, id],
    list: (params: GetAllAdminsParams) => [EQueryKeys.Admins, params],
    select: [EQueryKeys.AdminsToSelect],
  },
  businessLines: {
    all: [EQueryKeys.BusinessLines],
    detail: (id: string) => [EQueryKeys.BusinessLineDetails, id],
    list: (params: GetAllBusinessLinesParams) => [
      EQueryKeys.BusinessLines,
      params,
    ],
    select: [EQueryKeys.BusinessLinesToSelect],
  },
  companies: {
    detail: (ownerId: string) => [EQueryKeys.CompanyDetails, ownerId] as const,
  },
  documents: {
    detail: (id: string) => [EQueryKeys.DocumentDetails, id],
  },
  emails: {
    smartEventRequester: (serviceRequestId: string) => [
      EQueryKeys.SmartEventEmails,
      EQueryKeys.SmartEventRequesterEmails,
      serviceRequestId,
    ],
    smartEventProvider: (interventionId: string) => [
      EQueryKeys.SmartEventEmails,
      EQueryKeys.SmartEventProviderEmails,
      interventionId,
    ],
  },
  geographicAreas: {
    tree: () => [EQueryKeys.GeographicAreasTree],
  },
  interventions: {
    all: [EQueryKeys.Interventions],
    list: (params: GetAllInterventionsParams) => [
      EQueryKeys.Interventions,
      params,
    ],
    detail: (id: string) => [EQueryKeys.InterventionDetails, id],
    documents: (id: string) => [EQueryKeys.InterventionDocuments, id],
    notes: (id: string) => [EQueryKeys.InterventionNotes, id],
    timeline: (id: string) => [EQueryKeys.InterventionTimeline, id],
  },
  notes: {
    detail: (id: string) => [EQueryKeys.NoteDetails, id],
  },
  partnerships: {
    all: [EQueryKeys.Partnerships],
    detail: (id: string) => [EQueryKeys.PartnershipDetails, id],
    list: (params: GetAllPartnershipsParams) => [
      EQueryKeys.Partnerships,
      params,
    ],
  },
  providers: {
    all: [EQueryKeys.Providers],
    detail: (id: string) => [EQueryKeys.ProviderDetails, id],
    list: (params: GetAllProvidersParams) => [EQueryKeys.Providers, params],
    notes: (id: string) => [EQueryKeys.ProviderNotes, id],
    select: [EQueryKeys.ProvidersToSelect],
    workers: (params: GetAllWorkersParams) => [
      EQueryKeys.ProviderWorkers,
      params,
    ],
  },
  requesters: {
    detail: (id: string) => [EQueryKeys.RequesterDetails, id],
  },
  search: {
    query: (value: string) => [EQueryKeys.Search, value],
  },
  serviceRequests: {
    all: [EQueryKeys.ServiceRequests],
    list: (params: GetAllServiceRequestsParams) => [
      EQueryKeys.ServiceRequests,
      params,
    ],
    detail: (id: string) => [EQueryKeys.ServiceRequestDetails, id],
    allDetail: [EQueryKeys.ServiceRequestDetails],
    documents: (id: string) => [EQueryKeys.ServiceRequestDocuments, id],
    documentsWithInterventions: (id: string) => [
      EQueryKeys.ServiceRequestDocumentsWithInterventions,
      id,
    ],
    interventions: (serviceRequestId: string) => [
      EQueryKeys.ServiceRequestInterventions,
      serviceRequestId,
    ],
    notes: (id: string) => [EQueryKeys.ServiceRequestNotes, id],
    timeline: (id: string) => [EQueryKeys.ServiceRequestTimeline, id],
  },
  services: {
    all: [EQueryKeys.Services],
    detail: (id: string) => [EQueryKeys.ServiceDetails, id],
    list: (params: GetAllServicesParams) => [
      EQueryKeys.BusinessLineServices,
      params,
    ],
    listByBusinessLine: (businessLineId: string) => [
      EQueryKeys.BusinessLineServices,
      { businessLineId },
    ],
    select: (businessLineId?: string) => [
      EQueryKeys.ServicesToSelect,
      businessLineId,
    ],
  },
  serviceTypes: {
    select: [EQueryKeys.ServiceTypesToSelect],
  },
  serviceWorkflows: {
    select: (serviceTypeId: string) => [
      EQueryKeys.ServiceWorkflowsToSelect,
      serviceTypeId,
    ],
  },
  smartEvents: {
    timeline: (params: Pick<GetSmartEventTimelineParams, 'id' | 'type'>) => [
      EQueryKeys.SmartEventTimeline,
      params,
    ],
  },
  smartMatches: {
    interventions: (serviceRequestId: string) => [
      EQueryKeys.SmartMatchInterventions,
      serviceRequestId,
    ],
  },
  tasks: {
    all: [EQueryKeys.Tasks],
    allDetail: [EQueryKeys.TaskDetails],
    list: (params: GetAllTasksParams) => [EQueryKeys.Tasks, params],
    detail: (id: string) => [EQueryKeys.TaskDetails, id],
  },
  workflows: {
    select: [EQueryKeys.WorkflowsToSelect],
  },
} as const;
